import React from 'react';
import { Space, Tag, Tooltip } from 'antd';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import duration from 'dayjs/plugin/duration';
import { getLanguageFromUmiLocale } from '@/utils/locale';
import classNames from 'classnames';
import styles from './index.less';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import * as PreferencesSelectors from '@/selectors/preferencesSelectors';
import { useSelector } from 'umi';
// Extend dayjs with the relativeTime plugin
dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(duration);
dayjs.locale(getLanguageFromUmiLocale());

type IDueDateColoring = {}
const DEFAULT_DUE_DATE_COLORING = {
  overdue: 'rgba(255,77,79,0.31)',
  today: 'rgba(250,173,20,0.27)',
  tomorrow: 'rgba(157,168,2,0.25)',
  future: 'rgba(115,209,61,0.21)',
};

const getDueDateColoring = (date) => {
  const today = dayjs();
  const tomorrow = today.add(1, 'day');
  const dateObject = dayjs(date);
  if (dateObject.isBefore(today, 'day'))
    return DEFAULT_DUE_DATE_COLORING.overdue;

  if (dateObject.isSame(today, 'day'))
    return DEFAULT_DUE_DATE_COLORING.today;

  if (dateObject.isSame(tomorrow, 'day'))
    return DEFAULT_DUE_DATE_COLORING.tomorrow;

  return DEFAULT_DUE_DATE_COLORING.future;

};
export const PrettyDate = ({ date, dueDateColoring = false, absolute = false, format = 'YYYY-MM-DD HH:mm' }: {
  date: string,
  dueDateColoring?: boolean | IDueDateColoring,
  absolute?: boolean,
  format?: string
}) => {
  const selectedTimezone = useSelector(PreferencesSelectors.selectSelectedTimezone);

  if (!dayjs.isDayjs(date))
    date = dayjs(date);

  if (selectedTimezone)
    date = date.tz(selectedTimezone);

  // Relative by default (e.g. "2 days ago") with the exact date in the
  // tooltip; `absolute` swaps the two.
  const formattedDate = absolute ? date.format(format) : date.fromNow();
  const exactDate = absolute ? date.fromNow() : date.format(format);

  const color = dueDateColoring ? getDueDateColoring(date) : undefined;
  return (
    <Tooltip title={exactDate}>
      {color ? <Tag bordered={false} style={{ borderRadius: '100px', lineHeight: 1.5, color: '#333333' }}
                    color={color}>{formattedDate}</Tag> : <span style={{ color }}>{formattedDate}</span>}
    </Tooltip>
  );
};
const formatAppointmentDate = (date: dayjs.Dayjs): string => {
  const now = dayjs();
  const time = date.format('HH:mm');
  if (date.isSame(now, 'day')) return `Today ${time}`;
  if (date.isSame(now.add(1, 'day'), 'day')) return `Tomorrow ${time}`;
  if (date.isSame(now.subtract(1, 'day'), 'day')) return `Yesterday ${time}`;
  return date.format('YYYY-MM-DD HH:mm');
};

export const FormatDate = ({ format, date, className, onlyDate, onlyTime, appointmentDate, tz }: {
  date: string,
  format?: string,
  onlyDate?: boolean,
  className?: string,
  onlyTime?: boolean,
  appointmentDate?: boolean,
}) => {
  const selectedTimezone = useSelector(PreferencesSelectors.selectSelectedTimezone);

  if (!dayjs.isDayjs(date))
    date = dayjs(date);

  if (selectedTimezone)
    date = date.tz(selectedTimezone);


  if (appointmentDate) {
    return (
      <Tooltip title={selectedTimezone}>
        <span className={classNames(styles.formattedDate, className)}>{formatAppointmentDate(date)}</span>
      </Tooltip>
    );
  }

  if (!format) {
    format = 'YYYY-MM-DD HH:mm';
    if (onlyTime) format = 'HH:mm';
    if (onlyDate) format = 'YYYY-MM-DD';
  }

  const content = <span className={classNames(styles.formattedDate, className)}>{date.format(format)}</span>;

  if (format.includes('HH:mm'))
    return <Tooltip title={selectedTimezone}>
      {content}
    </Tooltip>;
  return content;
};

export const PrettyDateWithTime = ({ date }: { date: string }) => {
  const selectedTimezone = useSelector(PreferencesSelectors.selectSelectedTimezone);
  let dateObject = dayjs(date);
  if (selectedTimezone) {
    dateObject = dateObject.tz(selectedTimezone);
  }

  // Format the date to be relative (e.g., "2 days ago")
  const formattedDate = dateObject.fromNow();
  const formattedTime = dateObject.format('HH:mm');
  // Optionally, show a tooltip with the exact date
  const exactDate = dateObject.format('YYYY-MM-DD HH:mm');

  return (
    <Tooltip title={exactDate}>
      <Space>
        <span>{formattedDate}</span>
        <span>{formattedTime}</span>
      </Space>
    </Tooltip>
  );
};

export const PrettyDuration = ({ start, end }) => {
  const duration = dayjs.duration(dayjs(end).diff(dayjs(start)));

  const hours = Math.floor(duration.asHours());
  const minutes = duration.minutes();

  if (hours && minutes) return `${hours} h ${minutes} min`;
  if (hours) return `${hours} h`;
  return `${minutes} min`;
};

export const PrettyHour = ({ date }: { date: string }) => {
  const selectedTimezone = useSelector(PreferencesSelectors.selectSelectedTimezone);
  let dateObject = dayjs(date);
  if (selectedTimezone) {
    dateObject = dateObject.tz(selectedTimezone);
  }
  return dateObject.format('HH:mm');
};
export default PrettyDate;
